/**
 * Pure derivations behind the queue depth chart and the drain meters.
 *
 * These live outside the chart component so they can be tested directly —
 * the component itself needs a sized container and a ResizeObserver before it
 * renders anything at all.
 */
import { MAX_SAMPLE_GAP_MS } from './constants';
import { queueColor } from './queueDisplay';
import type { QueueMetrics, QueueSample } from './types';

export interface SeriesPoint {
  /** Epoch ms. */
  t: number;
  /** Messages pending. */
  v: number;
}

type NonEmpty<T> = [T, ...T[]];

const isNonEmpty = <T>(items: T[]): items is NonEmpty<T> => items.length > 0;

export interface Series {
  name: string;
  color: string;
  /** Every sampled point, oldest first. Non-empty by construction. */
  actual: NonEmpty<SeriesPoint>;
  /** `actual` split at sampling gaps, so the line breaks instead of bridging. */
  segments: SeriesPoint[][];
  /** Straight drain projection from the latest sample to zero, if estimable. */
  projection: readonly [SeriesPoint, SeriesPoint] | null;
  /** The queue is currently reporting an error, so its last point is stale. */
  stale: boolean;
}

/** Split one queue's history into contiguous runs, breaking at gaps and outages. */
const splitSegments = (history: QueueSample[], name: string): SeriesPoint[][] => {
  const segments: SeriesPoint[][] = [];
  let current: SeriesPoint[] = [];
  let prevTime: number | null = null;

  history.forEach((sample) => {
    const v = sample.pending[name];
    if (v == null) {
      if (current.length > 0) segments.push(current);
      current = [];
      prevTime = null;
      return;
    }
    if (prevTime != null && sample.time - prevTime > MAX_SAMPLE_GAP_MS) {
      segments.push(current);
      current = [];
    }
    current.push({ t: sample.time, v });
    prevTime = sample.time;
  });
  if (current.length > 0) segments.push(current);

  return segments;
};

/**
 * Build one series per queue that has at least one sampled point. Queues that
 * are currently erroring keep the history they did report — dropping them
 * would retroactively erase a backlog and read as a completed drain.
 */
export const buildSeries = (history: QueueSample[], queues: QueueMetrics[]): Series[] =>
  queues.flatMap((queue) => {
    const segments = splitSegments(history, queue.name);
    const actual = segments.flat();
    if (!isNonEmpty(actual)) return [];

    const stale = queue.error != null;
    const last = actual[actual.length - 1];
    const eta = queue.estimatedMinutesToClear;
    const projection: readonly [SeriesPoint, SeriesPoint] | null =
      !stale && last.v > 0 && eta != null && eta > 0 ?
        [last, { t: last.t + eta * 60_000, v: 0 }] :
        null;

    return [{ name: queue.name, color: queueColor(queue.name), actual, segments, projection, stale }];
  });

/**
 * Linear interpolation of a series' projected value at time t. Returns null
 * when there is nothing to project — callers render that as an em-dash rather
 * than as a zero.
 */
export const projectedValueAt = (series: Series, t: number): number | null => {
  if (!series.projection) {
    const last = series.actual[series.actual.length - 1];
    return last.v === 0 ? 0 : null;
  }
  const [from, to] = series.projection;
  if (t >= to.t) return 0;
  return from.v * (1 - (t - from.t) / (to.t - from.t));
};

/** Smallest of 1/2/5 × 10^k that is ≥ value. */
export const niceCeil = (value: number): number => {
  if (value <= 0) return 1;
  const power = Math.pow(10, Math.floor(Math.log10(value)));
  const candidate = [1, 2, 5, 10].find(c => c * power >= value) ?? 10;
  return candidate * power;
};

export interface ChartDomain {
  start: number;
  now: number;
  end: number;
  yMax: number;
  yStep: number;
}

/** Null until there is enough history to draw a trend. */
export const computeDomain = (history: QueueSample[], series: Series[]): ChartDomain | null => {
  if (history.length < 2 || series.length === 0) return null;

  const now = history[history.length - 1].time;
  // Pin at least a minute behind "now" so the first samples aren't squished
  // into a sliver while the history is still short.
  const start = Math.min(history[0].time, now - 60_000);
  const historySpan = Math.max(now - start, 60_000);
  const maxEtaMs = Math.max(
    0,
    ...series.flatMap(s => (s.projection ? [s.projection[1].t - now] : []))
  );
  const forecastSpan =
    maxEtaMs > 0 ?
      Math.min(maxEtaMs * 1.12, Math.max(historySpan * 2, 120_000), 2_400_000) :
      Math.max(historySpan * 0.15, 30_000);

  const rawMax = Math.max(1, ...series.flatMap(s => s.actual.map(p => p.v)));
  // Whole messages only — a fractional step renders duplicate tick labels
  // once the counts are rounded for display.
  const yStep = Math.max(1, niceCeil(rawMax / 4));

  return { start, now, end: now + forecastSpan, yMax: yStep * 4, yStep };
};

export const yAxisTicks = (domain: ChartDomain): number[] =>
  [0, 1, 2, 3, 4].map(i => i * domain.yStep);

/** One queue's sampled depths, outages dropped. */
export const pendingHistoryFor = (history: QueueSample[], name: string): number[] =>
  history.flatMap((sample) => {
    const v = sample.pending[name];
    return v == null ? [] : [v];
  });

/**
 * Share of the session's peak backlog that has cleared. The peak includes the
 * current depth so a queue whose history is missing (an outage, or the very
 * first paint) reads as 0% cleared rather than as fully drained.
 */
export const drainProgress = (pending: number, pendingHistory: number[]): number => {
  const peak = Math.max(pending, 0, ...pendingHistory);
  if (peak <= 0) return 1;
  return Math.min(1, Math.max(0, 1 - pending / peak));
};
